import type { ThemeV2, AtomDefaults, ColorSet } from './theme-v2-types';
import { lookupPreset, FONTS, type LookupPresetResult } from './shadcn-presets';
import { FONT_STACKS } from './font-stacks';
import type { DesignSystemAxes } from './axes-resolver';
import canonicalTheme from '../themes/default-theme.json';

/** The component set the platform declares — the base every style dresses. */
const CANONICAL_ATOMS = (canonicalTheme as { atoms?: Record<string, unknown> }).atoms;

export const DEFAULT_SPACING = {
  base: '0.25rem',
  scale: [0, 1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64, 80, 96]
};

export const DEFAULT_ELEVATION = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
};

export const DEFAULT_MOTION = {
  duration: { fast: '150ms', base: '200ms', slow: '300ms' },
  ease: {
    linear:   'linear',
    in:       'cubic-bezier(0.4, 0, 1, 1)',
    out:      'cubic-bezier(0, 0, 0.2, 1)',
    'in-out': 'cubic-bezier(0.4, 0, 0.2, 1)'
  }
};

export const DEFAULT_TYPOGRAPHY_SCALE = {
  xs:    { size: '0.75rem',  lineHeight: '1rem' },
  sm:    { size: '0.875rem', lineHeight: '1.25rem' },
  base:  { size: '1rem',     lineHeight: '1.5rem' },
  lg:    { size: '1.125rem', lineHeight: '1.75rem' },
  xl:    { size: '1.25rem',  lineHeight: '1.75rem' },
  '2xl': { size: '1.5rem',   lineHeight: '2rem' },
  '3xl': { size: '1.875rem', lineHeight: '2.25rem' },
  '4xl': { size: '2.25rem',  lineHeight: '2.5rem' }
};

export const DEFAULT_TYPOGRAPHY_WEIGHTS = {
  normal: 400, medium: 500, semibold: 600, bold: 700
};

// Atom defaults inferred per style — preserves visual parity with legacy 6-axis renders.
const STYLE_ATOM_DEFAULTS: Record<string, Record<string, AtomDefaults>> = {
  lyra:  { card: { defaultRadius: 'none', defaultShadow: 'sm',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } },
  luma:  { card: { defaultRadius: 'xl',   defaultShadow: 'md',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } },
  vega:  { card: { defaultRadius: 'lg',   defaultShadow: 'sm',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } },
  nova:  { card: { defaultRadius: 'lg',   defaultShadow: 'sm',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } },
  maia:  { card: { defaultRadius: 'md',   defaultShadow: 'sm',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } },
  mira:  { card: { defaultRadius: 'lg',   defaultShadow: 'sm',  defaultPadding: 'md' }, button: { defaultVariant: 'default', defaultSize: 'default' } }
};

function hashAxes(axes: DesignSystemAxes): string {
  return `${axes.style}-${axes.baseColor}-${axes.theme}-${axes.chart}-${axes.radius}-${axes.font}`
    .toLowerCase()
    .replace(/\s+/g, '-');
}

// Filter out non-color keys from ThemeVars (radius lives alongside colors in LookupPresetResult).
// Callers always pass `LookupPresetResult.vars`, which carries the required ColorSet keys
// (background, foreground, primary). TS can't narrow the rest-spread, so we cast through unknown.
function extractColorSet(vars: Record<string, string>): ColorSet {
  const { radius, ...colors } = vars;
  return colors as unknown as ColorSet;
}

function normalizeFontKey(fontName: string): string {
  // FONT_STACKS uses lowercase kebab-case keys (e.g. 'geist', 'dm-sans').
  // Axes may carry mixed-case (e.g. 'Geist', 'DM Sans'). Normalize defensively.
  return fontName.toLowerCase().replace(/\s+/g, '-');
}

function buildTypography(fontName: string) {
  const key = normalizeFontKey(fontName);
  const stack = FONT_STACKS[key]?.stack ?? FONTS[key]?.value ?? fontName;
  return {
    families: { sans: stack, mono: 'ui-monospace, SFMono-Regular, Menlo, monospace', heading: stack },
    scale:    DEFAULT_TYPOGRAPHY_SCALE,
    weights:  DEFAULT_TYPOGRAPHY_WEIGHTS
  };
}

export function axesToThemeV2(axes: DesignSystemAxes): ThemeV2 {
  const preset: LookupPresetResult = lookupPreset({
    style:      axes.style,
    baseColor:  axes.baseColor,
    theme:      axes.theme,
    chartColor: axes.chart,
    radius:     axes.radius,
    // Normalize font key so 'Geist' → 'geist' matches FONT_STACKS lookup inside lookupPreset.
    font:       normalizeFontKey(axes.font)
  });

  const radiusFromPreset = preset.cssVars.light.radius ?? '0.625rem';
  // The platform's declared component set is the BASE; the style table is an
  // overlay on it. Using the table as the whole map — which is what this line
  // used to do — left a resolved theme naming two components while
  // `themes/default-theme.json` declares 47, so the other 45 were not "at their
  // defaults" but absent, and nothing downstream could see them.
  const styleOverlay     = STYLE_ATOM_DEFAULTS[axes.style] ?? STYLE_ATOM_DEFAULTS.vega;
  const canonicalAtoms   = (CANONICAL_ATOMS ?? {}) as Record<string, AtomDefaults>;
  const atomDefaults: Record<string, AtomDefaults> = { ...canonicalAtoms };
  for (const [atom, overlay] of Object.entries(styleOverlay)) {
    atomDefaults[atom] = { ...(canonicalAtoms[atom] ?? {}), ...overlay };
  }

  return {
    $id:      `cogo:Theme/axes-derived/${hashAxes(axes)}`,
    version:  '2.0.0',
    metadata: { provenance: { axes: axes as unknown as Record<string, string> } },
    primitives: {
      colors:     { light: extractColorSet(preset.cssVars.light), dark: extractColorSet(preset.cssVars.dark) },
      typography: buildTypography(axes.font),
      spacing:    DEFAULT_SPACING,
      radius:     { base: radiusFromPreset },
      elevation:  DEFAULT_ELEVATION,
      motion:     DEFAULT_MOTION
    },
    atoms: atomDefaults,
    narrative: {},
    icons: { library: 'lucide' }
  };
}
